import { Autocomplete, TextField } from "@mui/material";
import { useMemo } from "react";

/**
 * IANA timezone picker.
 *
 * Quiet hours are wall-clock times in a named zone, so the zone has to be a
 * real IANA id - a hand-typed "CET" or "Stockholm" parses nowhere and turns a
 * quiet window into either a no-op or a permanent block. This replaces the free
 * text field that used to sit next to the quiet-hours times: the list comes from
 * the runtime itself (`Intl.supportedValuesOf`), so it can never drift from what
 * `Intl.DateTimeFormat` will accept.
 *
 * A value that is NOT in the list (an older row, or a zone this runtime does not
 * know) is kept and offered rather than silently dropped - editing a contact's
 * name must not rewrite its quiet-hours zone.
 */

/** Zones a Nordic operator actually picks, floated to the top of the list. */
const PREFERRED = [
  "Europe/Stockholm",
  "Europe/Oslo",
  "Europe/Copenhagen",
  "Europe/Helsinki",
  "Europe/London",
  "UTC",
];

/** `Intl.supportedValuesOf` is ES2022 and not in this project's lib target. */
type IntlWithSupportedValues = typeof Intl & {
  supportedValuesOf?: (key: "timeZone") => string[];
};

function allZones(): string[] {
  try {
    const supported =
      (Intl as IntlWithSupportedValues).supportedValuesOf?.("timeZone") ?? [];
    if (supported.length) return [...supported];
  } catch {
    // Older runtime - fall through to the short list below.
  }
  return [...PREFERRED];
}

interface Props {
  value: string;
  onChange: (tz: string) => void;
  label?: string;
  helperText?: string;
  testId?: string;
  disabled?: boolean;
}

export default function TimezoneSelect({
  value,
  onChange,
  label = "Timezone",
  helperText,
  testId,
  disabled,
}: Props) {
  const options = useMemo(() => {
    const zones = allZones();
    const rest = zones
      .filter((z) => !PREFERRED.includes(z))
      .sort((a, b) => a.localeCompare(b));
    const ordered = [...PREFERRED, ...rest];
    // Keep whatever is stored, even if this runtime has never heard of it.
    return value && !ordered.includes(value) ? [value, ...ordered] : ordered;
  }, [value]);

  return (
    <Autocomplete
      options={options}
      value={value || null}
      onChange={(_, v) => onChange(v ?? "")}
      disabled={disabled}
      disableClearable={false}
      autoHighlight
      fullWidth
      size="small"
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          inputProps={{
            ...params.inputProps,
            ...(testId ? { "data-testid": testId } : {}),
          }}
        />
      )}
    />
  );
}
